import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { DefaultSeoProps, NextSeo } from 'next-seo';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { Button, Heading } from '@appsumo/dorado-react';

import { EXCLUDE_ID_KEY } from '~/constants/discussions';
import {
  DEAL_COMMENT_TYPE,
  DISCUSSION,
  DISCUSSION_SECTION_ID,
} from '~/lib/discussions/constants';
import {
  DiscussionSortOption,
  DiscussionType,
  DiscussionComment,
} from '~/lib/discussions/types';
import useManualPagination from '~/lib/discussions/useDiscussionManualPagination';
import { useEvent } from '~/lib/events';
import { useExperiment } from '~/lib/experiment';
import { PDPContext, PDPContextType } from '~/lib/product/context';
import {
  isDealInactiveIndefinitely,
  skipQuantityRemaining,
} from '~/lib/product/utils';
import useUser from '~/lib/user';
import { getQueryFromPath, isExperimentalVariant } from '~/lib/util';
import { LTD_QUANTITY_PDP_EXPERIMENT_NAME } from '~/lib/util/constants';
import useDealCampaign from '~/hooks/deal-campaign';
import { useUserStatus } from '~/hooks/userStatus';
import { Deal } from '~/types/deal';

import CampaignBanner from '~/components/common/CampaignBanner';
import { DealCommentSearch, DealComments } from '~/components/product';
import CommentUserInformation from '~/components/product/Comments/CommentUserInformation';
import { Breadcrumb, Breadcrumbs } from '~/components/ui';

import AskSumoBox from './AskSumoBox';
import CollapsibleRatings from './CollapsibleRatings';
import DealRatings from './sidebar/DealRatings';
import ProductDetailRating from './sidebar/ProductDetailRating';
import AggregatePricingCard from './sidebar/PricingCard';

interface IAggregateProps {
  deal: Deal;
  title: string;
  seoOptions: DefaultSeoProps;
  discussionType: DiscussionType;
  commentType: DEAL_COMMENT_TYPE;
  sortOptions: DiscussionSortOption[];
  fallbackComments?: DiscussionComment;
}

function AggregateLayout({
  deal,
  title,
  seoOptions,
  discussionType,
  commentType,
  sortOptions,
  fallbackComments,
}: Readonly<IAggregateProps>) {
  const router = useRouter();
  const { user } = useUser();
  const { userStatus } = useUserStatus(deal.id);
  const { dealActiveAutoCampaign, campaignBannerContent, backgroundColor } =
    useDealCampaign(deal);
  const [showQuantityRemaining, setShowQuantityRemaining] = useState(false);

  const shouldSkipQuantityRemaining = useMemo(
    () => skipQuantityRemaining(deal),
    [deal],
  );

  const { variant: ltdQuantityVariant } = useExperiment(
    LTD_QUANTITY_PDP_EXPERIMENT_NAME,
    shouldSkipQuantityRemaining,
  );

  useEffect(() => {
    setShowQuantityRemaining(
      isExperimentalVariant(ltdQuantityVariant) && !shouldSkipQuantityRemaining,
    );
  }, [ltdQuantityVariant, shouldSkipQuantityRemaining]);

  const isInactiveIndefinitely = useMemo(
    () => isDealInactiveIndefinitely(deal.stage_details),
    [deal.stage_details],
  );

  const contextValue: PDPContextType = useMemo(
    () => ({
      deal,
      user,
      userStatus,
      showViewProductDetails: false,
      isInactiveIndefinitely,
      showQuantityRemaining,
    }),
    [deal, user, userStatus, isInactiveIndefinitely, showQuantityRemaining],
  );
  const [commentQuery, setCommentQuery] = useState('');
  const [excludeIds, setExcludeIds] = useState<string>('');
  const [discussionTotal, setDiscussionTotal] = useState<number>(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEvent('cart:added', () => router.push('/cart/'));

  useEvent('scroll:pricePlanSection', () => {
    router.push(`/products/${deal.slug}/#pricePlans`);
  });

  useIsomorphicLayoutEffect(() => {
    // if querying for comments, don't set total
    if (commentQuery) {
      return;
    }

    const count =
      discussionType === DISCUSSION.REVIEWS
        ? deal?.deal_review.review_count
        : deal?.deal_comment.comment_count;

    setDiscussionTotal(count || 0);
    setHasLoaded(true); // wait for the first render on browser to avoid hydration
  }, [deal, commentQuery]);

  const pageTitle = `${deal.public_name} ${title}`;
  const PDPUrl = `/products/${deal.slug}/?ref=${discussionType}`;

  const items: Breadcrumb[] = useMemo(
    () => [{ href: PDPUrl, label: deal.public_name }, { label: pageTitle }],
    [PDPUrl, deal.public_name, pageTitle],
  );

  const userIsInvalid = useMemo(() => {
    if (!user?.id || (!user?.email_verified && !user?.is_staff)) {
      return true;
    }

    return (
      !userStatus?.user_has_purchased &&
      !(deal.is_info || isInactiveIndefinitely)
    );
  }, [
    deal.is_info,
    isInactiveIndefinitely,
    user?.email_verified,
    user?.id,
    user?.is_staff,
    userStatus?.user_has_purchased,
  ]);

  const isReviewsPage = discussionType === DISCUSSION.REVIEWS;

  // preset query search from url
  useEffect(() => {
    const params = getQueryFromPath(router.asPath);
    const hasSearch = params.has('search');

    if (hasSearch) {
      setCommentQuery(params.get('search') as string);
    }

    if (params.has(EXCLUDE_ID_KEY)) {
      const excludeIdStr = params.get(EXCLUDE_ID_KEY) ?? '';

      // If somehow a user manually enters the exclude_id in the URL with a search, do not exclude
      if (!hasSearch) setExcludeIds(excludeIdStr);

      if (excludeIdStr) {
        const queryObj = { ...router.query };
        delete queryObj[EXCLUDE_ID_KEY];

        router.replace(
          {
            query: queryObj,
          },
          undefined,
          { shallow: true },
        );
      }
    }
  }, [router]);

  return (
    <PDPContext.Provider value={contextValue}>
      <NextSeo {...seoOptions} />

      {!!dealActiveAutoCampaign && campaignBannerContent && (
        <CampaignBanner
          bannerContent={campaignBannerContent}
          backgroundColor={backgroundColor}
          bannerBadge={dealActiveAutoCampaign?.campaign_config?.banner_badge}
          fontColor={dealActiveAutoCampaign?.campaign_config?.banner_font_color}
        />
      )}

      <div className="mx-auto my-4 w-full max-w-7xl grow p-4 sm:pt-0 md:my-6 lg:my-8 xl:my-10">
        <div className="mx-auto mb-4 max-w-6xl">
          <Breadcrumbs items={items} className="mb-4 lg:mb-6" />
          <Heading.H1 className="max-sm:text-2xl">{pageTitle}</Heading.H1>
        </div>
        <article className="mx-auto flex max-w-6xl gap-x-8 max-lg:flex-col">
          <div className="flex w-full flex-col gap-y-4 lg:max-w-[calc(100%-416px)]">
            <div className="lg:hidden">
              {/* Show detail ratings if on questions page */}
              {/* show breakout expandable section if on reviews */}
              {!isReviewsPage ? (
                <ProductDetailRating
                  dealReview={deal.deal_review}
                  reviewUrl={`/products/${deal.slug}/reviews`}
                  PDPUrl={PDPUrl}
                  showDealRatings
                />
              ) : (
                <CollapsibleRatings deal={deal} />
              )}
            </div>

            {isReviewsPage && deal.reviews_summary && (
              <AskSumoBox summary={deal.reviews_summary} />
            )}

            <div id={DISCUSSION_SECTION_ID}>
              <DealCommentSearch
                query={commentQuery}
                onChange={(query) => setCommentQuery(query)}
                placeholder={`Search ${discussionType}`}
                enableParamsState
              />

              <div className="flex flex-col gap-y-4 py-4">
                <DealComments
                  fallbackData={fallbackComments}
                  excludeIds={excludeIds}
                  type={commentType}
                  sortOptions={sortOptions}
                  total={discussionTotal}
                  updateTotal={setDiscussionTotal}
                  query={commentQuery}
                  enableParamsState
                  enableManualPagination
                  paginationHook={useManualPagination}
                />
              </div>
            </div>
          </div>

          <aside className="pb-8 lg:block lg:w-[384px] lg:min-w-[384px]">
            <AggregatePricingCard deal={deal} discussionType={discussionType} />

            {isReviewsPage && (
              <div className="hidden lg:block">
                <DealRatings deal={deal} />

                {!userIsInvalid && (
                  <Button
                    href={`/products/${deal.slug}/reviews/new/`}
                    disabled={
                      !userStatus?.user_has_purchased || user?.comment_blacklist
                    }
                    secondary
                  >
                    Write a review
                  </Button>
                )}
              </div>
            )}

            <div className="mt-6 hidden flex-col lg:flex">
              {userIsInvalid && hasLoaded && (
                <CommentUserInformation type={commentType} />
              )}
            </div>
          </aside>
        </article>
      </div>
    </PDPContext.Provider>
  );
}

export default AggregateLayout;
